import { useState } from 'react'
import { Drop } from '@phosphor-icons/react'
import { useFormat, useI18n } from '@/i18n'
import {
  Banner,
  BilingualLabel,
  Button,
  Card,
  CardBody,
  ChoiceGroup,
  Field,
  KeyValue,
  PickerSheet,
  Sheet,
} from '@/ui'
import { ActionBar } from '../../components/GateScreen'
import { useCodeLabel } from '../../components/labels'
import { canDivert, lotPlan } from '../../model/delivery'
import { moistureFigure, tenthsToString } from '../../model/moisture'
import { useWizard } from './context'
import { BiText } from '../../components/GateScreen'

/** F4.5 — भाव · Price: read-only price per lot from the card; Accept, or divert to dry (F4-B1.a). */
export function StepPrice({ onDivert }: { onDivert: (reasonCode: string) => Promise<void> }) {
  const { t } = useI18n()
  const fmt = useFormat()
  const { d, dispatch, cards, stamp } = useWizard()
  const [divert, setDivert] = useState(false)
  const plan = lotPlan(d)
  const fig = moistureFigure(d.moisture.readings)

  const problem = cards.problem
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardBody className="py-2">
          <KeyValue
            items={[
              {
                label: t('gate.price.moisture'),
                value: fig === null ? t('gate.price.notRead') : `${tenthsToString(fig)}%`,
                mono: true,
              },
              ...plan.map((l) => {
                const card = cards[l.stream]
                return {
                  label: t(l.stream === 'MAIN' ? 'gate.lot.goodBags' : 'gate.lot.redBags', {
                    count: l.bag_count,
                  }),
                  value:
                    card?.price_paise_per_kg != null
                      ? `${fmt.money(card.price_paise_per_kg)} / kg`
                      : '—',
                  hint: card
                    ? t('gate.price.version', { v: String(card.version ?? '1') })
                    : undefined,
                  mono: true,
                }
              }),
              { label: t('gate.price.deduction'), value: t('gate.price.none') },
            ]}
          />
        </CardBody>
      </Card>
      <Banner tone="info">{t('gate.price.paidOnProduce')}</Banner>
      {problem && (
        <Banner tone="error">
          {t(
            problem.kind === 'HAS_DEDUCTIONS'
              ? 'gate.price.cardHasDeductions'
              : 'gate.price.noCard',
            {
              lot: t(problem.stream === 'MAIN' ? 'gate.lot.good' : 'gate.lot.red'),
            },
          )}
        </Banner>
      )}

      <ActionBar>
        <Button
          size="xl"
          fullWidth
          disabled={!!problem}
          onClick={() => dispatch({ type: 'accept', at: stamp() })}
        >
          <BiText k="gate.price.accept" />
        </Button>
        <Button
          size="touch"
          variant="secondary"
          fullWidth
          leadingIcon={<Drop aria-hidden="true" />}
          onClick={() => setDivert(true)}
        >
          <BiText k="gate.price.divert" />
        </Button>
      </ActionBar>
      {divert && <DivertSheet onClose={() => setDivert(false)} onDivert={onDivert} />}
    </div>
  )
}

/** F4-B1.a — सुखाने के लिए वापस · Divert to drying. Nothing bought, no tag used. */
function DivertSheet({
  onClose,
  onDivert,
}: {
  onClose: () => void
  onDivert: (reasonCode: string) => Promise<void>
}) {
  const { t } = useI18n()
  const { d, roster } = useWizard()
  const code = useCodeLabel()
  const [choice, setChoice] = useState<'SELL' | 'DRY' | null>(null)
  const [reason, setReason] = useState<string | null>(roster.config.divert_reasons[0]?.code ?? null)
  const [busy, setBusy] = useState(false)
  const fig = moistureFigure(d.moisture.readings)
  const allowed = canDivert(d)
  return (
    <Sheet
      open
      onClose={onClose}
      title={<BilingualLabel k="gate.divert.title" />}
      footer={
        choice === 'DRY' ? (
          <Button
            size="touch"
            variant="danger"
            fullWidth
            disabled={!reason || !allowed}
            loading={busy}
            onClick={async () => {
              if (!reason) return
              setBusy(true)
              try {
                await onDivert(reason)
              } finally {
                setBusy(false)
              }
            }}
          >
            <BiText k="gate.divert.void" />
          </Button>
        ) : (
          <Button size="touch" variant="secondary" fullWidth onClick={onClose}>
            <BiText k="gate.divert.back" />
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-4">
        {fig !== null && (
          <KeyValue
            items={[
              { label: t('gate.price.moisture'), value: `${tenthsToString(fig)}%`, mono: true },
            ]}
          />
        )}
        <Field label={t('gate.divert.choice')} required>
          <ChoiceGroup
            options={[
              { value: 'SELL', label: t('gate.divert.sell') },
              { value: 'DRY', label: t('gate.divert.dry'), tone: 'warn' },
            ]}
            value={choice}
            onChange={(v) => (v === 'SELL' ? onClose() : setChoice(v))}
          />
        </Field>
        {choice === 'DRY' && (
          <>
            <Field label={t('gate.divert.reason')} required>
              <PickerSheet
                title={t('gate.divert.reason')}
                value={reason}
                onChange={setReason}
                options={roster.config.divert_reasons.map((r) => ({
                  value: r.code,
                  label: code(r),
                }))}
              />
            </Field>
            <KeyValue items={[{ label: t('gate.divert.tagsUsed'), value: '0', mono: true }]} />
            {!allowed && <Banner tone="error">{t('gate.divert.tagsFirst')}</Banner>}
            <Banner tone="info">{t('gate.divert.card')}</Banner>
          </>
        )}
      </div>
    </Sheet>
  )
}
